import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Platform, TextInputProps, View } from 'react-native';
import styled from 'styled-components/native';
import Colors from '../../../constants/Colors';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import FormElementQuestion from './components/FormElementQuestion';
import accessibilityTags from '../../../../accessibilityTags';

const minMultilineHeight = 128;

const FormInputView = styled.View`
  margin-top: 8px;
  width: 100%;
`;

const StyledTextInput = styled.TextInput<{
  hasHelperText?: boolean;
  minHeight?: number;
  multiline?: boolean;
  error?: boolean;
  empty?: boolean;
}>`
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  font-size: 16px;
  min-height: ${p =>
    p.minHeight
      ? `${p.minHeight}px`
      : p.multiline
      ? `${minMultilineHeight}px`
      : 'auto'};
  background-color: #fff;
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  border-radius: 8px;
  padding: ${Platform.OS === 'ios' || Platform.OS === 'web'
    ? '20px 16px'
    : '16px'};
  margin-bottom: ${p => (p.error || p.hasHelperText ? 4 : 12)}px;
` as React.FC<
  TextInputProps & {
    hasHelperText?: boolean;
    minHeight?: number;
    multiLine?: boolean;
    ref?: any;
    error?: boolean;
    empty?: boolean;
  }
>;

export type FormTextFieldConfig = {
  placeholder?: string;
  multiline?: boolean;
};

export type FormTextFieldProps<FormValuesType> = FormElementProps<
  FormValuesType,
  FormTextFieldConfig
> & {
  select?: boolean;
  multiline?: boolean;
  value?: string | number | boolean;
  onChange?: (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    value: FormValuesType[keyof FormValuesType]
  ) => void;
};

export const FormTextField = <FormValuesType extends object>({
  element,
  edit,
  disabled,
  select,
  children,
  onChange,
  ...props
}: PropsWithChildren<FormTextFieldProps<FormValuesType>>) => {
  const { formState, formValues, setFormValue } = useDynamicFormContext<
    FormValuesType,
    FormTextFieldConfig
  >();
  const key = (element.dataSource as keyof FormValuesType) || element.key;
  const { placeholder, multiline = props.multiline } = element.config || {};
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [height, setHeight] = useState<number>();
  const [value, setValue] = useState(props.value || formValues[key] || '');
  const onChangeRef = useRef(onChange);
  const previousValueRef = useRef(value);
  const cancelButtonRef = useRef<HTMLButtonElement>();

  // refresh value if formValues change outside of the component
  useEffect(() => {
    if (!focused && previousValueRef.current !== formValues[key]) {
      setValue(formValues[key] || '');
      previousValueRef.current = formValues[key] || '';
    }
  }, [focused, formValues, key]);
  return (
    <View>
      <FormElementQuestion element={element} />
      <FormInputView>
        <StyledTextInput
          multiline={multiline}
          blurOnSubmit={false}
          accessibilityLabel={accessibilityTags.textfieldID}
          testID={accessibilityTags.textfieldID}
          placeholderTextColor={Colors.placeholderColor}
          placeholder={placeholder}
          // error={Boolean(error)}
          // ref={(textInput: TextInput) => {
          //   textInputRef.current = textInput;
          //   addInput(textInput);
          // }}
          // {...props}
          minHeight={height}
          // variant={variant}
          value={value as string}
          empty={!value}
          // hasHelperText={Boolean(helperText)}
          // returnKeyType={returnKeyType}
          onChangeText={text => {
            setValue(text);
            setFormValue(key, text as any, element);
          }}
          onFocus={() => {
            setFocused(true);
            // setFocusedInput(textInputRef.current);
          }}
          onBlur={event => {
            setFocused(false);
            // handleBlur(name)(event);
            // setFocusedInput(undefined);
          }}
          onContentSizeChange={event => {
            let height = event.nativeEvent.contentSize.height;
            if (multiline && height < minMultilineHeight) {
              height = minMultilineHeight;
            }
            setHeight(height);
          }}
          // onSubmitEditing={() => {
          //   setFocused(false);
          //   Keyboard.dismiss();
          // }}
        />
      </FormInputView>
    </View>
  );
};

export const FormTextFieldPlugin: FormElementPlugin<any, FormTextFieldConfig> =
  {
    name: 'Text (free form)',
    type: 'TextField',
    dataTypes: ['text'],
    element: FormTextField as React.FC<
      FormElementProps<any, FormTextFieldConfig>
    >
  };
